/**
 * What the photograph is for, before the camera opens.
 *
 * Tapping „Foto" used to open the camera straight away, and nothing said why
 * one would point it at anything. The feature is the one that is hardest to
 * guess from its name: it does not store a picture, it reads the handwriting
 * and turns the mistakes into exercises.
 *
 * So the explanation stands where the question comes up — every time, not
 * only the first. Whoever knows it taps past in a second; whoever comes back
 * after two months gets it again.
 *
 * Below it, what has been photographed so far. Not as pictures — those are
 * not kept — but as the transcripts the app read, with the date and what came
 * of them. That is also where a page can be taken away again, which is the
 * remedy when the model misread a page and put nonsense into the rotation.
 */

import { useSignal } from '@preact/signals'
import { useEffect } from 'preact/hooks'
import * as store from '../editor/store.js'
import { PageBack } from './PageBack.js'
import { Icon } from './icons.js'
import { photo } from './PhotoPanel.js'

/** One row of the list: the page and how many mistakes came off it. */
interface Row {
  page: store.Page
  mistakes: number
}

export function PhotoStart({
  onTake, onClose, onChanged, onOpen,
}: {
  onTake: () => void
  onClose: () => void
  /** After a deletion: the exercise count outside has to be redone. */
  onChanged: () => void
  /** Put the page on the paper, as the text it is. */
  onOpen: (id: string) => void
}) {
  const rows = useSignal<Row[]>([])
  const loading = useSignal(true)
  /** Is the model reading right now? Then the button is a progress bar. */
  const p = photo.value
  const busy = p?.kind === 'wait'
  /** What the bar says: which page of how many, when there are several. */
  const busyLabel = p?.kind === 'wait' && p.total > 1
    ? `Kritzl liest Seite ${p.nr} von ${p.total} …`
    : 'Kritzl liest deine Schrift …'

  const load = async (): Promise<void> => {
    const list = await store.pages().catch(() => [])
    const all = await store.mistakes().catch(() => [])
    rows.value = list.map((page) => ({
      page,
      mistakes: all.filter((m) => m.pageId === page.id).length,
    }))
    loading.value = false
  }

  useEffect(() => { void load() }, [])
  // And again when the reading ends: several pages at once land here, on
  // this list, and it was loaded before they existed.
  useEffect(() => { if (!busy) void load() }, [busy])

  const remove = async (r: Row): Promise<void> => {
    // Say what goes with it. Throwing a page away also takes its mistakes out
    // of the exercises, and that is the whole reason one would do it — but it
    // must not come as a surprise.
    const n = r.mistakes
    const zusatz = n === 0 ? ''
      : n === 1 ? ' Der gefundene Fehler verschwindet dann auch aus den Übungen.'
      : ` Die ${n} gefundenen Fehler verschwinden dann auch aus den Übungen.`
    if (!confirm(`Diese Seite wegwerfen?${zusatz}`)) return
    await store.removePage(r.page.id).catch(() => {})
    await load()
    onChanged()
  }

  return (
    <div class="photostart">
      {/* The way back reads as a line, not as a second title: „‹ Übersicht"
          above the heading, the way one writes a path. */}
      <PageBack onBack={onClose}>
        <h1 class="page-title">Eine Seite fotografieren</h1>
      </PageBack>

      <div class="photostart-body">
        {/* One sentence, not three paragraphs.
        
            There used to be a lead, a list of three tips and the heading
            above them — more to read than the thing itself takes to do. What
            is left is what one cannot guess from the button: that the app
            reads the writing and makes exercises of it. How to hold a camera
            needs no instruction, and the picture not being kept is a promise
            that belongs where it is kept — under the button. */}
        <p class="photostart-lead">
          Kritzl liest deine Handschrift und macht Übungen aus den Wörtern,
          die noch nicht stimmen.
        </p>

        {/* While the model reads, the button IS the progress.
            
            A barber pole rather than a bar: nobody knows how long it takes,
            and a bar that fills at a guessed pace lies. Disabled, so a second
            tap cannot start a second reading — that would cost twice and
            leave two pages where one was meant. */}
        {busy ? (
          <div class="photostart-busy" role="status" aria-live="polite">
            <span class="photostart-busy-label">{busyLabel}</span>
          </div>
        ) : (
          <button type="button" class="action photostart-go" onClick={onTake}>
            <Icon name="camera" size={19} />
            Seite fotografieren
          </button>
        )}

        {!loading.value && rows.value.length > 0 && (
          <section class="photostart-list">
            <h2 class="photostart-head">Das hast du schon fotografiert</h2>
            {rows.value.map((r) => (
              <div key={r.page.id} class="photostart-row">
                {/* The whole row opens it. A transcript is a text like any
                    other — on the paper he can correct what the camera
                    found, have it checked again, and print it. */}
                <button
                  type="button"
                  class="photostart-row-text"
                  onClick={() => onOpen(r.page.id)}
                >
                  <span class="photostart-when">{when(r.page.at)}</span>
                  {/* The first line of the transcript, so a page can be told
                      from the others — there is no picture to recognise. */}
                  <span class="photostart-peek">{peek(r.page.text)}</span>
                  <span class="photostart-count">
                    {r.mistakes
                      ? `${r.mistakes} ${r.mistakes === 1 ? 'Stelle' : 'Stellen'} zum Üben`
                      : 'Nichts zu üben gefunden'}
                  </span>
                </button>
                <button
                  type="button"
                  class="photostart-drop"
                  aria-label={`Seite vom ${when(r.page.at)} wegwerfen`}
                  onClick={() => void remove(r)}
                >
                  <Icon name="trash-2" size={17} />
                </button>
              </div>
            ))}
          </section>
        )}
      </div>
    </div>
  )
}

/**
 * When, as a child would say it.
 *
 * „heute" and „gestern" rather than a date: those are the two that matter,
 * and a date says less about a page taken this morning.
 */
function when(at: number): string {
  const d = new Date(at)
  const today = new Date()
  const day = (x: Date): number =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  const diff = Math.round((day(today) - day(d)) / 86400000)
  if (diff === 0) return 'heute'
  if (diff === 1) return 'gestern'
  return d.toLocaleDateString('de-DE', { day: 'numeric', month: 'long' })
}

/** The first words of the transcript, enough to recognise the page by. */
function peek(text: string): string {
  const line = text.split('\n').find((l) => l.trim()) ?? ''
  const t = line.trim()
  return t.length <= 60 ? t : `${t.slice(0, 60).replace(/\s+\S*$/, '')}…`
}
